import { createSelector } from '@reduxjs/toolkit'
import { signIn } from 'components/Account/data/reducer'
import { RootState } from 'data/reducer'
import { useAppDispatch, useAppSelector } from 'data/store/hooks'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { calculateAndFormatDistance } from 'utils/formatters'

import CollectionForm from './CollectionForm/CollectionForm'
import CollectionList, { Action as ListAction, Item as ListItem } from './CollectionList'
import CollectionsCard, {
  Action as CardAction,
  FooterAction,
  Item as CardItem,
} from './CollectionsCard'
import {
  Collection as StateCollection,
  CollectionMap,
  Place as StatePlace,
  PlaceMap,
  Position,
  Status,
} from './data/model'
import { fetchCollectionsAndPlaces, setLastVisitedCollectionId } from './data/reducer'
import PlaceList, {
  Collection as PlacesCollection,
  Place as PlaceItem,
} from './PlaceList/PlaceList'
import { CollectionCategory } from './util/collection-category'

export interface CollectionsProps {
  className?: string
  onViewChange?: (view: ViewEvent) => void
  onItemHover?: (event: HoverEvent) => void
  view: ViewType
}

export interface HoverEvent {
  type: ListType
  id?: string
}

export enum ListType {
  places,
  collections,
}

export interface ViewEvent {
  type: ViewType
  payload?: unknown
}

export interface PdsData {
  id: string
  lat: number
  lng: number
  category: string | undefined
}

export enum ViewType {
  default = 'card',
  collectionsAdd = 'collectionsAdd',
  collectionsEdit = 'collectionsEdit',
  collectionsCard = 'collectionsCard',
  collectionsList = 'collectionsList',
  placesList = 'placesList',
  form = 'form',
}

export default function Collections({
  className,
  onViewChange,
  onItemHover,
  view,
}: CollectionsProps): JSX.Element {
  const dispatch = useAppDispatch()
  const { hash, pathname, search } = useLocation()
  const locationState = useMemo(
    () => ({ hash, pathName: pathname, search }),
    [hash, pathname, search]
  )

  const countryCode = useAppSelector((state) => state.user.countryCode)
  const unit = useAppSelector((state) => state.app.unit)

  // TODO this selector should be moved to the parent component, and passed as a prop
  const isUserLoggedIn: boolean = useAppSelector(
    (state: RootState) => state.account.user !== undefined
  )
  const { coordinates: userCurrentPosition } = useAppSelector((state) => state.user.currentPosition)

  // Memoize the output selector to avoid unnecessary re-renders
  const makeGetPlacesSelector = createSelector(
    [(state: RootState): PlaceMap => state.collections.placeMap],
    (placeMap: PlaceMap) => {
      return (collectionId: string) =>
        Object.values(placeMap).filter((place) => place.collectionId.includes(collectionId))
    }
  )
  const getPlaces = useAppSelector(makeGetPlacesSelector)

  // Memoize the output selector to avoid unnecessary re-renders
  const makeCollectionsSelector = createSelector(
    [(state: RootState): CollectionMap => state.collections.collectionMap],
    (collectionMap: CollectionMap) => [
      collectionMap,
      Object.values(collectionMap).map((collection) =>
        toCollectionItem(collection, getPlaces(collection.id).length)
      ),
    ]
  )
  const [collectionMap, collectionList] = useAppSelector(makeCollectionsSelector)
  const pageToken: string | undefined = useAppSelector((state) => state.collections.pageToken)
  const isLoading: boolean = useAppSelector(
    (state) => state.collections.status === Status.fetchCollectionsAndPlacesPending
  )
  const lastVisitedCollection = useAppSelector((state) => state.collections.lastVisitedCollectionId)

  const previousCollectionViewRef = useRef<ViewType>(ViewType.collectionsCard)
  const [selectedPlaces, setSelectedPlaces] = useState<PlaceItem[]>([])
  const [selectedCollection, setSelectedCollection] = useState<PlacesCollection>()
  const [selectedEditCollection, setSelectedEditCollection] = useState<PlacesCollection>()

  const updateView = useCallback(
    (type: ViewType, payload?: unknown): void => {
      onViewChange && onViewChange({ payload, type })
    },
    [onViewChange]
  )
  const handleCollectionEdit = useCallback(
    (collectionId: string): void => {
      const places = getPlaces(collectionId)
      setSelectedEditCollection(
        toSelectedCollection((collectionMap as CollectionMap)[collectionId], places.length)
      )
      updateView(ViewType.collectionsEdit)
      previousCollectionViewRef.current = ViewType.collectionsEdit
    },
    [collectionMap, getPlaces, updateView]
  )

  const handleCollectionSelect = useCallback(
    (collectionId: string): void => {
      dispatch(setLastVisitedCollectionId(collectionId))
      const places = getPlaces(collectionId)
      setSelectedCollection(
        toSelectedCollection((collectionMap as CollectionMap)[collectionId], places.length)
      )
      setSelectedPlaces(
        places.map((place) => toPlaceItem(place, userCurrentPosition, countryCode, unit))
      )
      updateView(
        ViewType.placesList,
        places.map((place) => toPdsData(place))
      )
    },
    [collectionMap, countryCode, dispatch, getPlaces, unit, updateView, userCurrentPosition]
  )

  const handlePlaceHover = useCallback(
    (id: string | undefined) => {
      if (onItemHover !== undefined) {
        onItemHover({ id, type: ListType.places })
      }
    },
    [onItemHover]
  )

  // Rehydrate from the global state the data from the last visited collection
  useEffect(() => {
    if (
      view === ViewType.placesList &&
      selectedCollection === undefined &&
      lastVisitedCollection !== undefined
    ) {
      handleCollectionSelect(lastVisitedCollection)
    }
  }, [handleCollectionSelect, lastVisitedCollection, selectedCollection, view])

  const handleOnScrollEnd = (): void => {
    if (isUserLoggedIn && pageToken !== undefined) {
      dispatch(fetchCollectionsAndPlaces(pageToken))
    }
  }

  const openInNewTab = (url: string): void => {
    window.open(url, '_blank')?.focus()
  }

  const handleActionClick = useCallback(
    (action: CardAction | ListAction): void => {
      switch (action) {
        case CardAction.login:
          dispatch(signIn({ ...locationState, action: 'sign-in' }))
          break
        case CardAction.seeAll:
          updateView(ViewType.collectionsList)
          previousCollectionViewRef.current = ViewType.collectionsList
          break
        case CardAction.add:
          updateView(ViewType.collectionsAdd)
          previousCollectionViewRef.current = ViewType.collectionsAdd
          break
        case ListAction.close:
          updateView(ViewType.collectionsCard)
          previousCollectionViewRef.current = ViewType.collectionsCard
          break
        default:
          // TODO this is a temporary solution, we will use a component to handle this
          openInNewTab('https://www.here.com/products/wego')
      }
    },
    [dispatch, locationState, updateView]
  )

  const handleOnPlaceListClose = useCallback((): void => {
    setLastVisitedCollectionId(undefined)
    updateView(previousCollectionViewRef.current)
  }, [updateView])

  return (
    <div className={className}>
      {view === ViewType.collectionsList && (
        <CollectionList
          onSelect={handleCollectionSelect}
          isLoading={isLoading}
          collections={collectionList as ListItem[]}
          onActionClick={handleActionClick}
          onEdit={handleCollectionEdit}
          onScrollEnd={handleOnScrollEnd}
        />
      )}
      {view === ViewType.collectionsCard && (
        <CollectionsCard
          onSelect={handleCollectionSelect}
          isLoading={isLoading}
          collections={collectionList as CardItem[]}
          footerAction={isUserLoggedIn ? FooterAction.add : FooterAction.login}
          onActionClick={handleActionClick}
        />
      )}
      {(view === ViewType.collectionsAdd || view === ViewType.collectionsEdit) && (
        <CollectionForm
          collection={selectedEditCollection}
          view={view}
          onActionClick={handleActionClick}
        />
      )}
      {view === ViewType.placesList && selectedCollection !== undefined && (
        <PlaceList
          collection={selectedCollection}
          places={selectedPlaces}
          onClose={handleOnPlaceListClose}
          onHover={handlePlaceHover}
        />
      )}
    </div>
  )
}

function toCollectionItem(collection: StateCollection, placesCount: number): CardItem | ListItem {
  return {
    category: collection.category as CollectionCategory,
    count: placesCount,
    id: collection.id,
    name: collection.name,
  }
}

function toSelectedCollection(collection: StateCollection, placesCount: number): PlacesCollection {
  return {
    category: collection.category as CollectionCategory,
    description: collection.description,
    id: collection.id,
    name: collection.name,
    placesCount: placesCount,
  }
}

function toPlaceItem(
  place: StatePlace,
  userPosition: Position | null,
  countryCode: string | null,
  unit: string
): PlaceItem {
  const distanceFromPoint =
    userPosition !== null
      ? calculateAndFormatDistance(userPosition, place.position, { countryCode, unit })
      : undefined

  return {
    address: place.address,
    categoryId: place.categoryId,
    createdTime: place.createdTime,
    distanceFromPoint,
    id: place.id,
    imageUrl: place.imageUrl,
    name: place.name,
    pdsUrl: `p/${encodeURIComponent(place.idV0)}`,
    position: place.position,
    rating: place.tripAdvisorRating?.average,
    ratingLink: place.tripAdvisorRating?.link,
    reviewCount: place.tripAdvisorRating?.count,
    updatedTime: place.updatedTime,
  }
}

function toPdsData(place: StatePlace): PdsData {
  return {
    category: place.categoryId,
    id: place.id,
    lat: place.position.lat,
    lng: place.position.lng,
  }
}
